import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d502Api, PendingItem } from '../../../services/d502Api';

export interface ItemState {
  item: PendingItem;
  selected: boolean;
}

export function useD502() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  /** Query pending receipt items by range */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    if (!startNo.trim() && !endNo.trim()) {
      message.warning('請輸入查詢範圍');
      return;
    }
    setLoading(true);
    setQueried(false);
    try {
      const res = await d502Api.query(startNo.trim(), endNo.trim());
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的待核准繳庫單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle checkbox for a single item */
  const handleToggleItem = useCallback((receiptNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fd120_01 === receiptNo ? { ...s, selected: checked } : s
      )
    );
  }, []);

  /** Select all / deselect all */
  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) =>
      prev.map((s) => ({
        ...s,
        selected: s.item.can_approve ? checked : s.selected,
      }))
    );
  }, []);

  /** Execute approval for all selected items */
  const handleExecute = useCallback(async (): Promise<boolean> => {
    const selectedNos = items
      .filter((s) => s.selected)
      .map((s) => s.item.fd120_01);

    if (selectedNos.length === 0) {
      message.warning('請先勾選要核准的繳庫單');
      return false;
    }

    setExecuteLoading(true);
    try {
      const res = await d502Api.execute(selectedNos);
      setLastApiResponse(res);
      if (res.failed === 0) {
        message.success(res.message || `核准完成，成功 ${res.success} 筆`);
      } else {
        message.warning(
          res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
        );
        if (res.results) {
          res.results
            .filter((r) => !r.success)
            .forEach((r) => message.error(`${r.receipt_no}: ${r.message}`));
        }
      }
      // Clear items after execution
      setItems([]);
      setQueried(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '核准執行失敗');
      return false;
    } finally {
      setExecuteLoading(false);
    }
  }, [items]);

  const selectedCount = items.filter((s) => s.selected).length;

  return {
    items,
    loading,
    executeLoading,
    queried,
    lastApiResponse,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  };
}
